/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { ChangeEvent, FC, KeyboardEvent, useCallback, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import { CalcReversalReq } from '../api/selfApi'
import '../assets/css/libs.css'
import '../assets/css/style.css'
import { fixedNumber, validateIsNumber, xorUndefined } from '../js/utils'
import { fetchCalcReversal } from '../store/modules/calcReversal'
import { constraintValues, defaultValues, FormInput } from './CalcReversal'
import { CalcReversalCalcButton } from './CalcReversalCalcButton'
import { CalcReversalResultComponent } from './CalcReversalResult'

interface CalcReversalFormProps {
  setInputErrorMessage: React.Dispatch<React.SetStateAction<string>>
  isPending: boolean
  isResponsive: boolean
}

type CalcReversalFormInputName =
  | 'initial'
  | 'addition'
  | 'interest1'
  | 'interest2'
  | 'startAge'
  | 'turningAddition'
  | 'turningAge'

function getDecimalPointLength(value: string) {
  if (isNaN(Number(value))) {
    return NaN
  }
  const numberParts = value.split('.')
  return numberParts[1] ? numberParts[1].length : 0
}

function makeRequest(arg: FormInput): CalcReversalReq {
  return {
    initial: Number(arg.initial) * 10000,
    addition: Number(arg.addition) * 10000,
    interest1: Number(arg.interest1),
    interest2: Number(arg.interest2),
    startAge: Number(arg.startAge),
    turningAge:
      arg.turningAge !== undefined && arg.turningAddition !== undefined
        ? Number(arg.turningAge)
        : undefined,
    turningAddition:
      arg.turningAge !== undefined && arg.turningAddition !== undefined
        ? Number(arg.turningAddition) * 10000
        : undefined,
  } as CalcReversalReq
}

export const CalcReversalForm: FC<CalcReversalFormProps> = React.memo(
  ({ setInputErrorMessage, isPending, isResponsive, children }) => {
    const dispatch = useDispatch()
    const { register, handleSubmit, setValue, getValues } = useForm<FormInput>()
    const [showTurningAgeAddition, setShowTurningAgeAddition] = useState(false)

    const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, isShow: boolean) => {
      e.preventDefault()
      setShowTurningAgeAddition(isShow)
    }

    const onSubmit = useCallback(
      (arg: FormInput) => {
        if (xorUndefined(arg.turningAddition, arg.turningAge)) {
          setInputErrorMessage(
            '取崩額の変更を設定した場合、取崩額（毎月）の下段にある、取崩額（変更後）、取崩額変更年齢を入力してください。'
          )
          return
        }
        if (arg.turningAge !== undefined && Number(arg.turningAge) <= Number(arg.startAge)) {
          setInputErrorMessage(
            '取崩額の変更を設定した場合、取崩額変更年齢は取崩開始年齢よりを大きな数値を設定してください。'
          )
          return
        }
        if (Number(arg.initial) < Number(arg.addition)) {
          setInputErrorMessage('取崩額より大きい初期残高額を設定して下さい。')
          return
        }
        const param = makeRequest(arg)
        dispatch(fetchCalcReversal(param))
      },
      [dispatch, setInputErrorMessage]
    )

    const handleChange = useCallback(
      (e: ChangeEvent<HTMLInputElement>, upper: number, lower: number, decimalPoint: number) => {
        const inputValue = e.target.value
        const inputName = e.target.name as CalcReversalFormInputName
        if (inputValue === '') {
          setValue(inputName, defaultValues[inputName], {
            shouldValidate: true,
          })
          return
        }
        if (!validateIsNumber(inputValue)) {
          setValue(inputName, defaultValues[inputName], {
            shouldValidate: true,
          })
          return
        }
        if (Number(inputValue) > upper) {
          setValue(inputName, defaultValues[inputName], {
            shouldValidate: true,
          })
          return
        }
        if (Number(inputValue) < lower) {
          setValue(inputName, defaultValues[inputName], {
            shouldValidate: true,
          })
          return
        }
        if (getDecimalPointLength(inputValue) > decimalPoint) {
          if (decimalPoint === 0) {
            setInputErrorMessage('小数点第1位を四捨五入して表示します。')
            setValue(inputName, Math.round(Number(inputValue)), {
              shouldValidate: true,
            })
            return
          } else {
            if (decimalPoint === 1) {
              setInputErrorMessage('小数点第2位を四捨五入して表示します。')
            } else {
              setInputErrorMessage('小数点第3位を四捨五入して表示します。')
            }
            setValue(inputName, Number(fixedNumber(inputValue, decimalPoint)), {
              shouldValidate: true,
            })
            return
          }
        }
        if (inputValue.match(/^0/) !== null) {
          setValue(inputName, Number(inputValue), {
            shouldValidate: true,
          })
        }
      },
      [setValue, setInputErrorMessage]
    )

    const handleUpDownKey = useCallback(
      (
        e: KeyboardEvent<HTMLInputElement>,
        inputName: CalcReversalFormInputName,
        step: number,
        decimalPoint: number
      ) => {
        const value = getValues(inputName)
        const nowValue = value !== undefined ? Number(value) : Number(defaultValues[inputName])

        if (e.key === 'ArrowUp') {
          const calcValue = nowValue + step
          const newValue =
            calcValue <= constraintValues[inputName].upper
              ? Number(fixedNumber(calcValue, decimalPoint))
              : constraintValues[inputName].upper
          setValue(inputName, newValue, { shouldValidate: true })
          e.preventDefault()
          return
        }
        if (e.key === 'ArrowDown') {
          const calcValue = nowValue - step
          const newValue =
            calcValue >= constraintValues[inputName].lower
              ? Number(fixedNumber(calcValue, decimalPoint))
              : constraintValues[inputName].lower
          setValue(inputName, newValue, { shouldValidate: true })
          e.preventDefault()
          return
        }
      },
      [getValues, setValue]
    )

    // <input> 要素を1つ上でラップしている <div> 要素ごと抽出（最小の役割の単位）
    const InputInitial = useCallback(() => {
      return (
        <div className="c-form-group">
          <span className="c-form-group__sup u-md-mt20px">保有資産</span>
          <span className="c-form-group__input">
            <input
              className="c-form__input u-text--right u-size--w140px"
              type="number"
              name="initial"
              step="any"
              defaultValue={defaultValues.initial}
              onKeyDown={(e) =>
                handleUpDownKey(e, 'initial', 10, constraintValues.initial.decimalPoint)
              }
              onBlurCapture={(e) =>
                handleChange(
                  e,
                  constraintValues.initial.upper,
                  constraintValues.initial.lower,
                  constraintValues.initial.decimalPoint
                )
              }
              ref={register({
                required: true,
                validate: {
                  isNumber: (value) => validateIsNumber(value),
                  upper: (value) => Number(value) <= constraintValues.initial.upper,
                  lower: (value) => Number(value) >= constraintValues.initial.lower,
                  decimal: (value) =>
                    getDecimalPointLength(value) <= constraintValues.initial.decimalPoint,
                },
              })}
            />
            <span className="c-form-group__text">万円</span>
          </span>
        </div>
      )
    }, [handleChange, handleUpDownKey, register])

    const InputAddition = useCallback(() => {
      return (
        <div className="c-form-group">
          <span className="c-form-group__sup notit">取り崩し額（毎月）</span>
          <span className="c-form-group__input">
            <input
              className="c-form__input u-text--right u-size--w100px"
              type="number"
              name="addition"
              step="0.1"
              defaultValue={defaultValues.addition}
              onKeyDown={(e) =>
                handleUpDownKey(e, 'addition', 0.1, constraintValues.addition.decimalPoint)
              }
              onBlurCapture={(e) =>
                handleChange(
                  e,
                  constraintValues.addition.upper,
                  constraintValues.addition.lower,
                  constraintValues.addition.decimalPoint
                )
              }
              ref={register({
                required: true,
                validate: {
                  isNumber: (value) => validateIsNumber(value),
                  upper: (value) => Number(value) <= constraintValues.addition.upper,
                  lower: (value) => Number(value) >= constraintValues.addition.lower,
                  decimal: (value) =>
                    getDecimalPointLength(value) <= constraintValues.addition.decimalPoint,
                },
              })}
            />
            <span className="c-form-group__text">万円</span>
          </span>
        </div>
      )
    }, [handleChange, handleUpDownKey, register])

    const InputInterest1 = useCallback(() => {
      return (
        <div className="c-form-group">
          <span className="c-form-group__sup notit">パターン1</span>
          <span className="c-form-group__input">
            <input
              className="c-form__input u-text--right u-size--w90px"
              type="number"
              name="interest1"
              step="0.1"
              defaultValue={defaultValues.interest1}
              onKeyDown={(e) =>
                handleUpDownKey(e, 'interest1', 0.1, constraintValues.interest1.decimalPoint)
              }
              onBlurCapture={(e) =>
                handleChange(
                  e,
                  constraintValues.interest1.upper,
                  constraintValues.interest1.lower,
                  constraintValues.interest1.decimalPoint
                )
              }
              ref={register({
                required: true,
                validate: {
                  isNumber: (value) => validateIsNumber(value),
                  upper: (value) => Number(value) <= constraintValues.interest1.upper,
                  lower: (value) => Number(value) >= constraintValues.interest1.lower,
                  decimal: (value) =>
                    getDecimalPointLength(value) <= constraintValues.interest1.decimalPoint,
                },
              })}
            />
            <span className="c-form-group__text">%</span>
          </span>
        </div>
      )
    }, [handleChange, handleUpDownKey, register])

    const InputInterest2 = useCallback(() => {
      return (
        <div className="c-form-group">
          <span className="c-form-group__sup notit">パターン2</span>
          <span className="c-form-group__input">
            <input
              className="c-form__input u-text--right u-size--w90px"
              type="number"
              name="interest2"
              step="0.1"
              defaultValue={defaultValues.interest2}
              onKeyDown={(e) =>
                handleUpDownKey(e, 'interest2', 0.1, constraintValues.interest2.decimalPoint)
              }
              onBlurCapture={(e) =>
                handleChange(
                  e,
                  constraintValues.interest2.upper,
                  constraintValues.interest2.lower,
                  constraintValues.interest2.decimalPoint
                )
              }
              ref={register({
                required: true,
                validate: {
                  isNumber: (value) => validateIsNumber(value),
                  upper: (value) => Number(value) <= constraintValues.interest2.upper,
                  lower: (value) => Number(value) >= constraintValues.interest2.lower,
                  decimal: (value) =>
                    getDecimalPointLength(value) <= constraintValues.interest2.decimalPoint,
                },
              })}
            />
            <span className="c-form-group__text">%</span>
          </span>
        </div>
      )
    }, [handleChange, handleUpDownKey, register])

    const InputStartAge = useCallback(() => {
      return (
        <div className="c-form-group">
          <span className="c-form-group__sup notit">開始年齢</span>
          <span className="c-form-group__input">
            <input
              className="c-form__input u-text--right u-size--w80px"
              type="number"
              name="startAge"
              step="1"
              defaultValue={defaultValues.startAge}
              onKeyDown={(e) =>
                handleUpDownKey(e, 'startAge', 1, constraintValues.startAge.decimalPoint)
              }
              onBlurCapture={(e) =>
                handleChange(
                  e,
                  constraintValues.startAge.upper,
                  constraintValues.startAge.lower,
                  constraintValues.startAge.decimalPoint
                )
              }
              ref={register({
                required: true,
                validate: {
                  isNumber: (value) => validateIsNumber(value),
                  upper: (value) => Number(value) <= constraintValues.startAge.upper,
                  lower: (value) => Number(value) >= constraintValues.startAge.lower,
                  decimal: (value) =>
                    getDecimalPointLength(value) <= constraintValues.startAge.decimalPoint,
                },
              })}
            />
            <span className="c-form-group__text">歳</span>
          </span>
        </div>
      )
    }, [handleChange, handleUpDownKey, register])

    const InputTurningAge = useCallback(() => {
      return (
        <div className="c-form-group">
          <span className={`c-form-group__sup ${isResponsive ? 'notit' : ''}`}>
            取り崩し変更年齢
          </span>
          <span className="c-form-group__input">
            <input
              className="c-form__input u-text--right u-size--w80px"
              type="number"
              name="turningAge"
              defaultValue={defaultValues.turningAge}
              step="1"
              onKeyDown={(e) =>
                handleUpDownKey(e, 'turningAge', 1, constraintValues.turningAge.decimalPoint)
              }
              onBlurCapture={(e) =>
                handleChange(
                  e,
                  constraintValues.turningAge.upper,
                  constraintValues.turningAge.lower,
                  constraintValues.turningAge.decimalPoint
                )
              }
              ref={register({
                required: true,
                validate: {
                  isNumber: (value) => validateIsNumber(value),
                  upper: (value) => Number(value) <= constraintValues.turningAge.upper,
                  lower: (value) => Number(value) >= constraintValues.turningAge.lower,
                  decimal: (value) =>
                    getDecimalPointLength(value) <= constraintValues.turningAge.decimalPoint,
                },
              })}
            />
            <span className="c-form-group__text">歳</span>
          </span>
        </div>
      )
    }, [handleChange, handleUpDownKey, isResponsive, register])

    const InputTurningAddition = useCallback(() => {
      return (
        <div className="c-form-group">
          <span className={`c-form-group__sup ${isResponsive ? 'notit' : ''}`}>
            取り崩し変更額（毎月）
          </span>
          <span className="c-form-group__input">
            <input
              className="c-form__input u-text--right u-size--w100px"
              type="number"
              name="turningAddition"
              defaultValue={defaultValues.turningAddition}
              step="0.1"
              onKeyDown={(e) =>
                handleUpDownKey(
                  e,
                  'turningAddition',
                  0.1,
                  constraintValues.turningAddition.decimalPoint
                )
              }
              onBlurCapture={(e) =>
                handleChange(
                  e,
                  constraintValues.turningAddition.upper,
                  constraintValues.turningAddition.lower,
                  constraintValues.turningAddition.decimalPoint
                )
              }
              ref={register({
                required: true,
                validate: {
                  isNumber: (value) => validateIsNumber(value),
                  upper: (value) => Number(value) <= constraintValues.turningAddition.upper,
                  lower: (value) => Number(value) >= constraintValues.turningAddition.lower,
                  decimal: (value) =>
                    getDecimalPointLength(value) <= constraintValues.turningAddition.decimalPoint,
                },
              })}
            />
            <span className="c-form-group__text">万円</span>
          </span>
        </div>
      )
    }, [handleChange, handleUpDownKey, isResponsive, register])

    return (
      <React.Fragment>
        <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
          <div className="p-simulation__grid-3">
            <div className="p-simulation__grid-3__col">
              <InputInitial />
            </div>
            <div className="p-simulation__grid-3__col u-size--w380px">
              <p className="p-simulation__grid-3__title">取り崩し設定</p>
              <div className="c-grid">
                <div className="c-grid__col">
                  <InputStartAge />
                </div>
                <div className="c-grid__col">
                  <InputAddition />
                </div>
              </div>
              {!showTurningAgeAddition && (
                <div className="u-mt8px u-mb20px u-mr16px">
                  <a
                    href="#"
                    className="p-simulation__setting--button c-button-arrow"
                    onClick={(e) => handleClick(e, true)}
                  >
                    <span>
                      取り崩し年齢と取り崩し額を
                      <br />
                      途中で変更する
                    </span>
                  </a>
                </div>
              )}

              {showTurningAgeAddition && (
                <div className="c-grid">
                  <div className="c-grid__col">
                    <InputTurningAge />
                  </div>
                  <div className="c-grid__col">
                    <InputTurningAddition />
                  </div>
                  <a
                    href="#"
                    className="p-simulation__setting__close"
                    onClick={(e) => handleClick(e, false)}
                  >
                    ×
                  </a>
                </div>
              )}
            </div>
            <div className="p-simulation__grid-3__col">
              <p className="p-simulation__grid-3__title">運用した場合のリターン（年率）</p>
              <div className="c-grid">
                <div className="c-grid__col">
                  <InputInterest1 />
                </div>
                <div className="c-grid__col">
                  <InputInterest2 />
                </div>
              </div>
            </div>
          </div>
          <CalcReversalCalcButton isPending={isPending} isResponsive={isResponsive} />
          <CalcReversalResultComponent isResponsive={isResponsive} />
        </form>
      </React.Fragment>
    )
  }
)
